import './App.css';
import Skeleton from "./Skeleton";

import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';

// API
import {
  getUserProfile,
  getUserSocialLinks,
  claimTap,
} from './api/apiService';

// FingerprintJS Pro
import FingerprintJS from '@fingerprintjs/fingerprintjs-pro';

// Images
import ChippedLogo from './assets/images/chipped_logo_black.png';

const STOREFRONT_URL = "https://chippedsocial.com";

/**
 * Map platform names -> CSS classnames used in your icon sprite/styles.
 */
const getPlatformClassName = (platform) => {
  const platformMap = {
    'farcaster': 'farcaster',
    'lens': 'lens',
    'telegram': 'telegram',
    'instagram': 'instagram',
    'tiktok': 'tiktok',
    'twitter': 'twitter',
    'x': 'twitter', // treat "x" as Twitter
    'linkedin': 'linkedin',
    'tinder': 'tinder',
    'custom': 'hyperlink',
    'hyperlink': 'hyperlink',
  };

  if (!platform || typeof platform !== 'string') return 'hyperlink';
  return platformMap[platform.toLowerCase()] || 'hyperlink';
};

/**
 * Given:
 *  - socialLinks (new format from /users/:user_id/social-links)
 *  - profileData (fallback legacy fields on the user object)
 *
 * Return an array of { link, active, className } for rendering.
 */
const getSocialLinksToRender = (socialLinks, profileData) => {
  // New format takes priority
  if (Array.isArray(socialLinks) && socialLinks.length > 0) {
    return socialLinks
      .filter(link => link.is_active)
      .map(link => ({
        link: link.url,
        active: true,
        className: getPlatformClassName(link.platform),
      }));
  }

  // Fallback to old-style fields on profileData
  if (profileData) {
    return [
      {
        link: profileData.farcaster_link,
        active: profileData.farcaster_active,
        className: 'farcaster',
      },
      {
        link: profileData.lens_link,
        active: profileData.lens_active,
        className: 'lens',
      },
      {
        link: profileData.telegram_link,
        active: profileData.telegram_active,
        className: 'telegram',
      },
      {
        link: profileData.instagram_link,
        active: profileData.instagram_active,
        className: 'instagram',
      },
      {
        link: profileData.tiktok_link,
        active: profileData.tiktok_active,
        className: 'tiktok',
      },
      {
        link: profileData.twitter_link,
        active: profileData.twitter_active,
        className: 'twitter',
      },
      {
        link: profileData.custom_link,
        active: profileData.custom_active,
        className: 'hyperlink',
      },
    ].filter(item => item.active && item.link);
  }

  return [];
};

function App() {
  const { user_id } = useParams();
  const location = useLocation();

  const [profileData, setProfileData] = useState(null);
  const [socialLinks, setSocialLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  //
  // 1. Load profile + socials
  //
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const [profileResponse, socialLinksResponse] = await Promise.allSettled([
          getUserProfile(user_id),
          getUserSocialLinks(user_id),
        ]);

        if (profileResponse.status === 'fulfilled') {
          setProfileData(profileResponse.value);
        } else {
          // If we couldn't fetch profile, bounce to storefront
          setNotFound(true);
          return;
        }

        if (socialLinksResponse.status === 'fulfilled') {
          setSocialLinks(socialLinksResponse.value || []);
        } else {
          console.warn('Failed to fetch new social links format, using fallback');
          setSocialLinks([]);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user_id]);

  //
  // 2. After profile is loaded, try to claim the tap
  //
  useEffect(() => {
    if (!profileData || loading || notFound) return;

    const queryParams = new URLSearchParams(location.search);
    const fromRedirect = queryParams.get('fromRedirect');
    const tagId = queryParams.get('tag_id');
    const token = queryParams.get('token');

    async function attemptClaimTap() {
      // Only attempt to claim if:
      // - We got here from the NFC redirect flow
      // - We have both tagId and token
      if (fromRedirect !== 'true' || !tagId || !token) {
        return;
      }

      // Soft anti-abuse: only run this on actual mobile devices
      const userAgentIsMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      const hasTouchSupport =
        'ontouchstart' in window || (navigator.maxTouchPoints > 0);
      const isMobileDevice = userAgentIsMobile && hasTouchSupport;

      if (!isMobileDevice) {
        console.log('Not a mobile device - fingerprint generation skipped.');
        return;
      }

      try {
        // FingerprintJS Pro
        const fp = await FingerprintJS.load({
          token: 'uquLgmuxXdKStBb4rbC7',
        });
        const result = await fp.get();
        const requestId = result.requestId;
        const sealedResult = result.sealedResult;

        // Unified payload for /nfc/validate-and-award
        const payload = {
          user_id,
          tag_id: tagId,
          token,
          requestId,
          sealedResult,
          // We're not including target_url here because this is the profile view,
          // not the direct-link shim.
          meta: { ts: Date.now(), via: 'profile' },
        };

        const claimResponse = await claimTap(payload);
        console.log('claimTap response:', claimResponse);

        // Future: you could inspect claimResponse.awarded_reward or claimResponse.awarded_competition
        // and show a toast / badge animation, etc.
      } catch (error) {
        console.error('Error generating fingerprint or claiming tap:', error);
      }
    }

    attemptClaimTap();
  }, [profileData, loading, notFound, user_id, location.search]);

  //
  // 3. If user doesn't exist, redirect to storefront
  //
  if (notFound && !loading) {
    window.location.replace(STOREFRONT_URL);
    return null;
  }

  //
  // 4. Loading skeleton UI while profile fetch is in progress
  //
  if (loading) {
    return (
      <div className="profile-container">
        <div className='header'>
          <img className='header-logo' src={ChippedLogo} alt="Chipped Logo" />
        </div>

        <div className='profile-hero'>
          <div className='profile-picture-container'>
            <Skeleton width={145} height={145} circle />
          </div>

          <div className='display-name'>
            <Skeleton width={110} height={22} style={{ margin: '10px 0 4px 0' }} />
          </div>

          <div className='username'>
            <Skeleton width={70} height={16} />
          </div>

          <div className='icon-container'>
            <div className='icons-row' style={{ display: 'flex', gap: 8 }}>
              {Array(7).fill(0).map((_, i) => (
                <Skeleton key={i} width={32} height={32} circle />
              ))}
            </div>
          </div>
        </div>

        <div className='bio-container'>
          <div className='section-title'>
            <Skeleton width={40} height={18} />
          </div>
          <div className='section-text'>
            <Skeleton width={200} height={14} style={{ marginBottom: 8 }} />
            <Skeleton width={160} height={14} style={{ marginBottom: 8 }} />
            <Skeleton width={180} height={14} />
          </div>
        </div>
      </div>
    );
  }

  //
  // 5. Final profile render
  //
  return (
    <div className="profile-container">
      <div className='header'>
        <img className='header-logo' src={ChippedLogo} alt="Chipped Logo" />
      </div>

      <div className='profile-hero'>
        <div className='profile-picture-container'>
          <img
            className='profile-picture'
            src={profileData.profile_picture}
            alt="Profile"
          />
        </div>

        <div className='display-name'>{profileData.display_name}</div>
        <div className='username'>@{profileData.username}</div>

        <div className='icon-container'>
          <div className='icons-row'>
            {getSocialLinksToRender(socialLinks, profileData).map(
              (social, index) => (
                <a
                  key={index}
                  className='social-icon'
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className={`svg-icon ${social.className}`}></div>
                </a>
              )
            )}
          </div>
        </div>
      </div>

      <div className='bio-container'>
        <div className='section-title'>BIO</div>
        <div className='section-text'>{profileData.bio}</div>
      </div>
    </div>
  );
}

export default App;
